import React, { createContext, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import PermissionApi, { TestRequestPermissions } from "../api/services/PermissionApi";
import ErrorSnackbarContext from "./ErrorSnackbarContext";
import GenericPermissionContext from "./GenericPermissionContext";
import CustomLoading from "../components/Alert/CustomLoading";
import useEnv from "../hooks/env";
import HiddenError from "../models/HiddenError";
import useTestRequestParams, { TestRequestTabType } from "../pages/Requests/useTestRequestParams";

const hasTabAccess = (tab: TestRequestTabType, permissions: TestRequestPermissions): boolean | undefined => {
  switch (tab) {
    case TestRequestTabType.DESCRIPTION:
      return permissions.accessDescription;
    case TestRequestTabType.SUBSTRATE:
      return permissions.accessSubstrateSamples;
    case TestRequestTabType.PRODUCT:
      return permissions.accessProductSamples;
    case TestRequestTabType.TEST:
      return permissions.accessTestPlan;
    case TestRequestTabType.REPORT:
      return permissions.accessReports;
    case TestRequestTabType.HISTORY:
      return permissions.accessHistory || permissions.accessReports;
    case TestRequestTabType.APROE_PROJECT:
      return permissions.accessProjectInfo;
    default:
      return false;
  }
};

export const TestRequestPermissionContext = createContext<
  TestRequestPermissions & {
    initiallyLoaded: boolean;
    updatePermissionState: () => Promise<TestRequestPermissions>;
  }
>({
  updatePermissionState: () => Promise.reject(),
  initiallyLoaded: false,
});

export const TestRequestPermissionContextProvider: React.FunctionComponent = (
  props: React.PropsWithChildren<Record<string, unknown>>,
) => {
  const { api } = useEnv();
  const permissionApi = new PermissionApi(api);
  const history = useHistory();
  const { testRequestId, tab } = useTestRequestParams();
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);
  const [permissions, setPermissions] = useState<TestRequestPermissions>({});
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { newTestRequests } = useContext(GenericPermissionContext);
  const { children } = props;

  const updatePermissionState = (initialLoading?: boolean) => {
    if (testRequestId === "new") {
      return Promise.resolve().then(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const purpose = searchParams.get("aproe") === "1" ? "APPLICATION_PROCESS" : "DEFAULT";
        const newTestRequest = newTestRequests[purpose] || {};
        setPermissions(newTestRequest);
        return newTestRequest;
      });
    }
    return permissionApi
      .fetchTestRequestPermissions(testRequestId)
      .then((response) => {
        setPermissions(response.data);
        return response.data;
      })
      .then((testRequestPermissions) => {
        // TODO workaround: remove once tab types moved to strings instead indeces
        let checkTab = tab;
        if (testRequestPermissions.viewProjectInfo && checkTab === TestRequestTabType.SUBSTRATE) {
          checkTab = TestRequestTabType.APROE_PROJECT;
        }
        if (!hasTabAccess(checkTab, testRequestPermissions)) {
          if (tab === TestRequestTabType.DESCRIPTION) {
            history.push("/requests");
          } else {
            history.push(`/requests/${testRequestId}`);
          }
          if (initialLoading) {
            throw new Error("Access to this section of the test request denied");
          } else {
            // do not show error, if permission state changes during interactions
            throw new HiddenError("Access to this section of the test request denied");
          }
        }
        return testRequestPermissions;
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 404) {
          history.push("/requests");
          if (initialLoading) {
            throw new Error("This test request can not be found");
          } else {
            // do not show error, if permission state changes during interactions
            throw new HiddenError("This test request can not be found");
          }
        } else if (status === 400 || status === 403) {
          history.push("/requests");
          if (initialLoading) {
            throw new Error("Access to the test request denied");
          } else {
            // do not show error, if permission state changes during interactions
            throw new HiddenError("Access to the test request denied");
          }
        }
        throw error;
      });
  };

  const refresh = () => {
    setInitiallyLoaded(false);
    updatePermissionState(true)
      .catch(handleHttpError)
      .finally(() => {
        setInitiallyLoaded(true);
      });
  };

  useEffect(() => {
    refresh();
  }, [testRequestId]);

  return (
    <TestRequestPermissionContext.Provider
      value={{
        ...permissions,
        initiallyLoaded,
        updatePermissionState,
      }}
    >
      {!initiallyLoaded && <CustomLoading />}
      {initiallyLoaded && children}
    </TestRequestPermissionContext.Provider>
  );
};

export default TestRequestPermissionContext;
